import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import intl from "react-intl-universal";
import { TbDotsVertical } from "react-icons/tb";
import {
  PORTAL_DROPDOWN_BACKDROP_Z_INDEX,
  PORTAL_DROPDOWN_Z_INDEX,
  usePortalDropdownPosition,
} from "../../../../Hooks/usePortalDropdownPosition";
import { resolveBaasPresenceDot } from "../../../../Utils/baasPresenceDot";

/**
 * Presence ball: green = live SignalR, red = has Conta Digital but offline,
 * orange = Sem conta / Aguardando primeiro acesso / console-only.
 * Always rendered so offline state is visible (never hide the ball).
 * Lives inside the NOME cell — never as its own table column.
 */
export const BaasLoggedInDot = ({
  loggedIn = false,
  hasAccount,
  accessStatus,
  titleKey = "baas.users.loggedIn",
  offlineTitleKey = "baas.users.loggedOut",
  noAccountTitleKey = "baas.users.noAccount",
}) => {
  const status = resolveBaasPresenceDot({ loggedIn, hasAccount, accessStatus });
  const title =
    status === "online"
      ? intl.get(titleKey)
      : status === "none"
        ? accessStatus === "pending"
          ? intl.get("baas.users.access.status.pending")
          : intl.get(noAccountTitleKey)
        : intl.get(offlineTitleKey);
  const colorClass =
    status === "online"
      ? "bg-emerald-500"
      : status === "none"
        ? "bg-orange-500"
        : "bg-red-500";
  return (
    <span
      className={`inline-block h-2 w-2 shrink-0 rounded-full ${colorClass}`}
      title={title}
      aria-label={title}
    />
  );
};

/**
 * Compact gold console action menu — Detalhes always; optional extras via `items`
 * (e.g. Onboarding interno for PF, Deslogar when wired). Prefer `items` when the
 * row has more than Detalhes + Deslogar. Omit logout for BUSINESS / negócio rows.
 * Menu is portaled + fixed so it is not clipped or stranded in empty table space.
 */
export const BaasRowActionMenu = ({
  detailsLabel,
  onDetails,
  logoutLabel,
  onLogout,
  logoutDisabled = false,
  items,
}) => {
  const [open, setOpen] = useState(false);
  const { triggerRef, menuRef, menuStyle } = usePortalDropdownPosition(open, 180);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const showLogout = typeof onLogout === "function" && logoutLabel;
  const menuItems = Array.isArray(items)
    ? items.filter((item) => item && item.label)
    : [
        {
          id: "details",
          label: detailsLabel || intl.get("common.details"),
          onClick: onDetails,
        },
        showLogout
          ? {
              id: "logout",
              label: logoutLabel,
              onClick: onLogout,
              disabled: logoutDisabled,
            }
          : null,
      ].filter(Boolean);

  const alignedStyle = (() => {
    if (!menuStyle || !triggerRef.current) return menuStyle;
    const rect = triggerRef.current.getBoundingClientRect();
    return {
      ...menuStyle,
      left: "auto",
      width: "auto",
      minWidth: "9.5rem",
      right: Math.max(8, window.innerWidth - rect.right),
    };
  })();

  const closeMenu = (event) => {
    event?.stopPropagation();
    setOpen(false);
  };

  return (
    <div className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        className="grid-history-action-btn !h-8 !min-h-8 shrink-0 !px-2"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={intl.get("baas.users.columns.actions")}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        <TbDotsVertical className="h-3.5 w-3.5" />
      </button>
      {open && alignedStyle && typeof document !== "undefined"
        ? createPortal(
            <>
              <div
                className="fixed inset-0"
                style={{ zIndex: PORTAL_DROPDOWN_BACKDROP_Z_INDEX }}
                onClick={closeMenu}
                aria-hidden="true"
              />
              <div
                ref={menuRef}
                role="menu"
                style={{ ...alignedStyle, zIndex: PORTAL_DROPDOWN_Z_INDEX }}
                className="overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-brand-dark-border dark:bg-brand-dark-elevated"
                onClick={(event) => event.stopPropagation()}
              >
                {menuItems.map((item) => (
                  <button
                    key={item.id || item.label}
                    type="button"
                    role="menuitem"
                    disabled={Boolean(item.disabled)}
                    title={item.title}
                    className={`block w-full px-3 py-1.5 text-left text-xs font-medium hover:bg-brand-gold/10 disabled:cursor-not-allowed disabled:opacity-40 ${
                      item.danger
                        ? "text-red-600 dark:text-red-400"
                        : "text-brand-dark dark:text-gray-100"
                    }`}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (item.disabled) return;
                      setOpen(false);
                      item.onClick?.();
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  );
};
